import { spacing, breakpoints } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { GridRow } from '@naf/grid';
import { Text } from '@naf/text';
import { isNotTransientProp } from '../../../../lib/shouldForwardProp';

const calculateElementWidth = ({
  width,
  center,
  left,
  right,
  padding,
}: {
  width: number;
  center?: boolean;
  left?: boolean;
  right?: boolean;
  padding?: number;
}) => {
  const leftBorderPixels = center || left ? 1 : 0;
  const rightBorderPixels = center || right ? 1 : 0;
  const totalBorderPixels = leftBorderPixels + rightBorderPixels + (padding ?? 0);
  const calculatedInnerWidth = width - totalBorderPixels;
  return `${calculatedInnerWidth}px`;
};

export const borderColor = nafColor.neutral.neutral3;

export const ScrollContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  position: relative;
  background-color: ${nafColor.signature.white};

  @media (max-width: ${breakpoints.xl}) {
    overflow-x: scroll;
  }
`;

export const TableContainer = styled.div`
  width: max-content;
  overflow-y: visible;
  background-color: ${nafColor.signature.white};
`;

export const Th = styled.div<{ $alignLeft?: boolean; $width?: number; $right?: boolean; $left?: boolean }>`
  padding: ${spacing.space16} 0;
  font-weight: 500;
  align-items: center;
  justify-content: ${({ $alignLeft }) => ($alignLeft ? 'flex-start' : 'center')};
  display: flex;
  min-height: 50px;
  ${({ $left }) =>
    $left
      ? `
          border-left: 1px solid ${borderColor};
        `
      : null}
  ${({ $right }) =>
    $right
      ? `
          border-right: 1px solid ${borderColor};
        `
      : null}
  width: ${({ $width, $left, $right }) =>
    $width
      ? calculateElementWidth({ width: $width, left: $left, right: $right })
      : calculateElementWidth({ width: 309, left: $left, right: $right })};
  padding: 0 !important;

  @media (max-width: ${breakpoints.m}) {
    padding: ${spacing.space8} 0;
  }
`;

export const StickyTh = styled(Th, { shouldForwardProp: isNotTransientProp })<{ $right?: boolean }>`
  position: sticky;
  left: 0;
  background: white;
  z-index: 3;
  width: ${({ $right }) => ($right ? calculateElementWidth({ width: 180, right: $right }) : '180px')};
  padding: 0 !important;

  @media (max-width: ${breakpoints.m}) {
    width: ${({ $right }) => ($right ? calculateElementWidth({ width: 150, right: $right }) : '150px')};
  }
`;

export const ModelColumn = styled(StickyTh, { shouldForwardProp: isNotTransientProp })<{ $right?: boolean }>`
  border-right: 1px solid ${borderColor};
  padding: ${spacing.space16} 0 !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

// Important is used to override styles inherited from Article
export const ModelSubtitle = styled(Text)`
  font-size: 1rem !important;
  color: ${nafColor.neutral.neutral6};
  line-height: 1 !important;
  margin: 8px 0 0 !important;
`;

export const StyledHeaderText = styled(Text)`
  font-weight: 600;
  margin-left: ${spacing.space8} !important;
`;

export const Tr = styled(GridRow, { shouldForwardProp: isNotTransientProp })<{ $hideBorder?: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 180px 309px 309px 206px;
  grid-gap: 0 !important;
  border-bottom: ${({ $hideBorder }) => ($hideBorder ? '' : `1px solid ${borderColor}`)};

  @media (max-width: ${breakpoints.m}) {
    grid-template-columns: 145px 309px 309px 206px;
  }
`;

export const Td = styled.div<{ $center?: boolean }>`
  text-align: right;
  display: flex;
  height: 100%;
  ${({ $center }) =>
    $center
      ? css`
          border-left: 1px solid ${borderColor};
          border-right: 1px solid ${borderColor};
        `
      : null}
`;

export const Cell = styled.div<{
  $left?: boolean;
  $center?: boolean;
  $right?: boolean;
  $value?: number | null;
  $negativeValue?: number | null;
  $padding?: number;
}>`
  display: flex;
  padding: ${spacing.space16} 0;
  width: ${({ $center, $left, $right, $padding }) =>
    calculateElementWidth({ width: 103, center: $center, left: $left, right: $right, padding: $padding })};
  padding-left: ${({ $padding }) => ($padding ? `${$padding}px` : '0')};
  padding-right: ${({ $padding }) => ($padding ? `${$padding}px` : '0')};
  justify-content: center;
  align-items: center;
  text-align: center;
  border-left: ${({ $center, $left }) => ($center || $left ? '1px' : '0px')} solid ${borderColor};
  border-right: ${({ $center, $right }) => ($center || $right ? '1px' : '0px')} solid ${borderColor};
  ${({ $value }) =>
    $value
      ? `
          color: ${$value > 0 ? nafColor.information.successDark : nafColor.information.errorDark};
        `
      : null}
  ${({ $negativeValue }) =>
    $negativeValue
      ? `
          color: ${$negativeValue > 0 ? nafColor.information.errorDark : nafColor.information.successDark};
        `
      : null}
`;

export const TooltipWrapper = styled.div`
  margin-left: ${spacing.space8};
  height: 26px;
  position: relative;
  z-index: 1000;

  > * > div > span {
    min-width: 300px;
  }

  @media (min-width: ${breakpoints.m}) {
    > * > div > span {
      min-width: 200px;
      transform: translate(-100%, calc(100% + 30px));
    }
  }

  @media (max-width: ${breakpoints.m}) {
    > * > div > span {
      transform: translateY(100%);
      min-width: 150px;
    }
  }
`;
